// Bevestigingspagina van de iDEAL-betaalflow (herbestel-afrekenen
// 17-08). De klant landt hier na de bank (redirect_url of cancel_url
// van MultiSafepay), met alleen de eigen betaalreferentie in de URL —
// nooit een token of klantdata.
//
// De browser-redirect is nooit de bron van de betaalstatus: de pagina
// vraagt de status aan onze eigen API, en zolang die "gestart" zegt
// pollt hij rustig door (de server doet zelf de MSP-GET-fallback).
// Betaald = groene succeskaart met spaarpunten en verwachte bezorgdag;
// mislukt/afgebroken/verlopen = neutrale kaart met een opnieuw-knop
// die via sessionStorage terug kan naar de eigen bestellink.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { herbestelApi } from "@/lib/api";
import type { HerbestelBetalingStatusAntwoord } from "@/types/api";
import {
  RETOUR_KAART,
  RETOUR_KOLOM,
  RetourFooter,
  RetourHeader,
  UspBalk,
} from "../Retour/RetourShopElementen";
import { HERBESTEL_TOKEN_OPSLAG } from "./HerbestelPagina";

const SHOP_URL = "https://www.voeronline.nl";
const GROEN = "#2E9E5B";
const ORANJE = "#ef7f01";
const PUNTEN_KLEUR = "#8a5a1a";

// Zolang de bank-bevestiging onderweg is pollen we even door; daarna
// toont de pagina eerlijk "nog even geduld" zonder eindeloos verkeer.
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20;

function bezorgdagTekst(iso: string | null, taal: string): string | null {
  if (!iso) return null;
  const datum = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(datum.getTime())) return null;
  return new Intl.DateTimeFormat(taal === "en" ? "en-GB" : "nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(datum);
}

export function HerbestelBevestiging() {
  const { t, i18n } = useTranslation();
  const [zoekParams] = useSearchParams();
  const referentie = zoekParams.get("ref");
  const afgebroken = zoekParams.get("afgebroken") === "1";

  const [antwoord, setAntwoord] =
    useState<HerbestelBetalingStatusAntwoord | null>(null);
  const [onbekend, setOnbekend] = useState(!referentie);
  const polls = useRef(0);

  useEffect(() => {
    if (!referentie) return;
    let actief = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const haal = () => {
      herbestelApi
        .betalingStatus(referentie)
        .then((data) => {
          if (!actief) return;
          setAntwoord(data);
          if (data.status === "gestart" && polls.current < MAX_POLLS) {
            polls.current += 1;
            timer = setTimeout(haal, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (actief) setOnbekend(true);
        });
    };
    haal();
    return () => {
      actief = false;
      if (timer) clearTimeout(timer);
    };
  }, [referentie]);

  // "Opnieuw proberen": naar het aanvraagscherm (fixronde V1, blok DX).
  // Na een betaling is de bestellink ingetrokken (A2) en na een
  // afgebroken betaling kan hij verlopen zijn; de bewaarde token zou de
  // klant dan op "link werkt niet meer" laten landen. Het aanvraagscherm
  // stuurt in één klik een verse link. De bewaarde token gaat weg, anders
  // pakt de pagina hem uit sessionStorage weer op.
  const opnieuwUrl = "/herbestellen";
  const vergeetToken = () => {
    try {
      sessionStorage.removeItem(HERBESTEL_TOKEN_OPSLAG);
    } catch {
      // Geen opslag beschikbaar: dan valt er ook niets te vergeten.
    }
  };

  const status = antwoord?.status ?? null;
  const laden = !onbekend && antwoord === null;
  const wachten = status === "gestart";
  const betaald = status === "betaald";
  // De status is leidend: MSP stuurt óók een geweigerde betaling naar
  // de cancel-URL (live gemeten), dus de afgebroken-vlag telt alleen
  // zolang er nog geen eindstatus is.
  const toonGeannuleerd =
    status === "geannuleerd" ||
    (afgebroken &&
      status !== "mislukt" &&
      status !== "verlopen" &&
      !betaald);
  const mislukTitel = toonGeannuleerd
    ? t("herbestel.bevestiging_geannuleerd_titel")
    : status === "verlopen"
      ? t("herbestel.bevestiging_verlopen_titel")
      : t("herbestel.bevestiging_mislukt_titel");
  const mislukTekst = toonGeannuleerd
    ? t("herbestel.bevestiging_geannuleerd")
    : status === "verlopen"
      ? t("herbestel.bevestiging_verlopen")
      : t("herbestel.bevestiging_mislukt");

  const bezorgdag = bezorgdagTekst(
    antwoord?.bezorgdag ?? null,
    i18n.language,
  );

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <UspBalk />
      <main className={`${RETOUR_KOLOM} flex-1 py-8`}>
        {laden ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : onbekend ? (
          <div data-testid="bevestiging-onbekend" className={RETOUR_KAART}>
            <p className="text-[14px] leading-relaxed text-[#333333]">
              {t("herbestel.bevestiging_onbekend")}
            </p>
          </div>
        ) : betaald ? (
          <div
            data-testid="bevestiging-betaald"
            className={`${RETOUR_KAART} text-center`}
          >
            <span
              aria-hidden
              className="mx-auto grid h-16 w-16 place-items-center rounded-full"
              style={{ background: GROEN }}
            >
              <Check className="h-8 w-8 text-white" strokeWidth={3} />
            </span>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-brand-navy">
              {t("herbestel.bevestiging_titel")}
            </h1>
            <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
              {t("herbestel.bevestiging_betaald")}{" "}
              {antwoord?.ordernummer
                ? t("herbestel.bevestiging_ordernummer", {
                    nummer: antwoord.ordernummer,
                  })
                : t("herbestel.bevestiging_verwerken")}
            </p>
            {bezorgdag && (
              <p
                data-testid="bevestiging-bezorgdag"
                className="mt-2 text-[14px] font-bold"
                style={{ color: GROEN }}
              >
                {t("herbestel.bevestiging_bezorgdag", {
                  datum: bezorgdag,
                })}
              </p>
            )}
            {(antwoord?.punten ?? 0) > 0 && (
              <span
                data-testid="bevestiging-punten"
                className="mt-3 inline-block rounded-full border px-3 py-1 text-[12.5px]"
                style={{
                  color: PUNTEN_KLEUR,
                  background: "#fff9f3",
                  borderColor: "#f7dfc2",
                }}
              >
                {t("herbestel.bevestiging_punten", {
                  count: antwoord?.punten ?? 0,
                })}
              </span>
            )}
            <p className="mt-5">
              <a
                href={SHOP_URL}
                className="rounded-sm text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline"
              >
                {t("herbestel.bevestiging_naar_shop")}
              </a>
            </p>
          </div>
        ) : wachten ? (
          <div
            data-testid="bevestiging-wachten"
            className={`${RETOUR_KAART} text-center`}
          >
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-400" />
            <h1 className="mt-4 text-xl font-bold tracking-tight text-brand-navy">
              {t("herbestel.bevestiging_wachten_titel")}
            </h1>
            <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
              {t("herbestel.bevestiging_wachten")}
            </p>
          </div>
        ) : (
          <div
            data-testid="bevestiging-mislukt"
            className={`${RETOUR_KAART} text-center`}
          >
            <h1 className="text-xl font-bold tracking-tight text-brand-navy">
              {mislukTitel}
            </h1>
            <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
              {mislukTekst}
            </p>
            <p className="mt-5">
              <a
                href={opnieuwUrl}
                onClick={vergeetToken}
                data-testid="bevestiging-opnieuw"
                className="inline-block rounded-[10px] px-6 py-3.5 text-[15px] font-bold text-white"
                style={{ background: ORANJE }}
              >
                {t("herbestel.bevestiging_opnieuw")}
              </a>
            </p>
          </div>
        )}
      </main>
      <RetourFooter />
    </div>
  );
}
